import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/Button'
import type { PageMeta } from '@/types/api'
import { cn } from '@/utils/cn'
import { formatNumber } from '@/utils/format'

interface PaginationProps {
  meta: PageMeta | undefined
  onPageChange: (page: number) => void
  onPerPageChange?: (perPage: number) => void
  className?: string
}

function pageWindow(current: number, total: number): (number | 'gap')[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const pages = new Set([1, total, current - 1, current, current + 1])
  const sorted = [...pages].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b)
  const out: (number | 'gap')[] = []
  sorted.forEach((p, i) => {
    const prev = sorted[i - 1]
    if (i > 0 && prev !== undefined && p - prev > 1) out.push('gap')
    out.push(p)
  })
  return out
}

export function Pagination({ meta, onPageChange, onPerPageChange, className }: PaginationProps) {
  const { t } = useTranslation()
  if (!meta || meta.total === 0) return null
  const from = (meta.page - 1) * meta.perPage + 1
  const to = Math.min(meta.total, meta.page * meta.perPage)

  return (
    <nav
      aria-label={t('Pagination')}
      className={cn(
        'flex flex-col items-center justify-between gap-3 border-t border-line px-5 py-3 sm:flex-row',
        className,
      )}
    >
      <p className="text-xs text-muted">
        <Trans
          t={t}
          i18nKey="Showing <b>{{from}}</b>–<b>{{to}}</b> of <b>{{total}}</b>"
          values={{ from: formatNumber(from), to: formatNumber(to), total: formatNumber(meta.total) }}
          components={{ b: <span className="font-medium text-ink tabular" /> }}
        />
      </p>
      <div className="flex items-center gap-3">
        {onPerPageChange ? (
          <label className="hidden items-center gap-2 text-xs text-muted sm:flex">
            {t('Rows')}
            <select
              value={meta.perPage}
              onChange={(e) => onPerPageChange(Number(e.target.value))}
              className="h-8 rounded-control border border-line bg-surface px-2 text-xs text-ink focus:border-brand-400 focus:ring-4 focus:ring-brand-500/15 focus:outline-none"
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('Previous page')}
            disabled={meta.page <= 1}
            onClick={() => onPageChange(meta.page - 1)}
          >
            <ChevronLeft />
          </Button>
          {pageWindow(meta.page, meta.totalPages).map((p, i) =>
            p === 'gap' ? (
              <span key={`gap-${i}`} className="px-1 text-xs text-subtle" aria-hidden="true">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p)}
                aria-current={p === meta.page ? 'page' : undefined}
                aria-label={t('Page {{n}}', { n: p })}
                className={cn(
                  'hidden h-8 min-w-8 rounded-control px-2 text-xs font-medium tabular transition-colors sm:inline-block',
                  p === meta.page
                    ? 'bg-brand-600 text-white shadow-xs'
                    : 'text-ink-soft hover:bg-surface-sunken',
                )}
              >
                {p}
              </button>
            ),
          )}
          <span className="px-2 text-xs text-muted sm:hidden">
            {meta.page} / {meta.totalPages}
          </span>
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('Next page')}
            disabled={meta.page >= meta.totalPages}
            onClick={() => onPageChange(meta.page + 1)}
          >
            <ChevronRight />
          </Button>
        </div>
      </div>
    </nav>
  )
}
